import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../../components/Loading";
import { MainButton } from "../../../components/MainButton";
import { ConfigType } from "../../../contexts/AppConfigContext";
import { useAppConfig } from "../../../hooks/useAppConfig";
import { useSeller } from "../../../hooks/useSeller";
import api from "../../../services/api";
import Swal from "sweetalert2";
import "./info.css";
import InputMask from "react-input-mask";

// Função para formatar CPF
function formatDocument(doc: string) {
    if (!doc) return "";
    const cleanDoc = doc.replace(/\D/g, "");
    if (cleanDoc.length === 11) {
        // CPF: 000.000.000-00
        return cleanDoc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    if (cleanDoc.length === 14) {
        // CNPJ: 00.000.000/0000-00
        return cleanDoc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
    return doc;
}

function formatLabelDocument(doc: string) {
    if (!doc) return "CPF";
    const cleanDoc = doc.replace(/\D/g, "");
    if (cleanDoc.length === 11) {
        // CPF: 000.000.000-00
        return "CPF";
    }
    if (cleanDoc.length === 14) {
        // CNPJ: 00.000.000/0000-00
        return "CNPJ";
    }
    return doc;
}

type SellerInfo = {
    id: number;
    pluginspaceId: number;
    name: string;
    email: string;
    cpf: string;
    numberPhone: string | null;
    image: string;
    status: boolean;
    created: string;
    updated: string;
    content: string;
    allowDiscount?: boolean;
    discountValue?: number;
    changePassword: number;
    countLogin: number;
    lastLogin: string | null;
};

export function SellerInfo() {
    const navigate = useNavigate();
    const { pluginspace_id } = useParams();
    const { getAppConfig } = useAppConfig();
    const { getTokenSellertoRequest, getSellerUser } = useSeller();

    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState<ConfigType | void>();
    const [sellerInfo, setSellerInfo] = useState<SellerInfo>({
        id: 0,
        pluginspaceId: 0,
        name: "",
        email: "",
        cpf: "",
        numberPhone: null,
        image: "default/default-user.jpg",
        status: true,
        created: "",
        updated: "",
        content: "{}",
        changePassword: 0,
        countLogin: 0,
        lastLogin: null,
    });
    const [isEditing, setIsEditing] = useState(false);

    async function getConfig() {
        setConfig(await getAppConfig());
        await getSellerInfo();
        setLoading(false);
    }

    async function getSellerInfo() {
        try {
            setLoading(true);

            const sellerUser = await getSellerUser();

            if (!sellerUser) {
                setLoading(false);
                return;
            }

            await api
                .get(`/api/users/${sellerUser.id}`, {
                    headers: {
                        Authorization: `Bearer ${await getTokenSellertoRequest()}`,
                    },
                })
                .then((response: any) => {
                    if (response.data) {
                        const data = response.data;

                        // Parse do content JSON para extrair configurações extras
                        let parsedContent: any = {};
                        try {
                            parsedContent = JSON.parse(data.content || "{}");
                        } catch (e) {
                            console.log("Erro ao fazer parse do content:", e);
                        }

                        setSellerInfo({
                            ...data,
                            allowDiscount: parsedContent.allow_discount === "true",
                            discountValue: parseFloat(parsedContent.discount_value || "0"),
                        });
                    }
                    setLoading(false);
                })
                .catch((err: any) => {
                    setLoading(false);
                    console.log("Erro ao buscar informações do vendedor:", err);
                    // Dados de exemplo baseados na estrutura real da API
                    setSellerInfo({
                        id: 33,
                        pluginspaceId: 352,
                        name: "Fred o Vende Tudos",
                        email: "fredtudo@gmail.com",
                        cpf: "50034682023",
                        numberPhone: null,
                        image: "default/default-user.jpg",
                        status: true,
                        created: "2024-05-10T15:54:48.194Z",
                        updated: "2024-05-10T15:54:48.194Z",
                        content: '{"allow_discount":"true","discount_value":"5"}',
                        changePassword: 1,
                        countLogin: 0,
                        lastLogin: null,
                        allowDiscount: true,
                        discountValue: 5,
                    });
                });
        } catch (error) {
            console.log(error);
            setLoading(false);
        }
    }

    const handleSaveInfo = async () => {
        try {
            setLoading(true);

            // Validação dos dados
            if (!sellerInfo.name.trim()) {
                setLoading(false);
                Swal.fire({
                    icon: "warning",
                    title: "Atenção!",
                    text: "O nome não pode estar vazio.",
                });
                return;
            }
            if (!sellerInfo.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sellerInfo.email)) {
                setLoading(false);
                Swal.fire({
                    icon: "warning",
                    title: "Atenção!",
                    text: "Informe um e-mail válido.",
                });
                return;
            }
            if (sellerInfo.numberPhone && sellerInfo.numberPhone.replace(/\D/g, "").length < 10) {
                setLoading(false);
                Swal.fire({
                    icon: "warning",
                    title: "Atenção!",
                    text: "Informe um telefone válido.",
                });
                return;
            }

            // Preparar dados para envio - apenas campos editáveis
            const updateData = {
                name: sellerInfo.name,
                email: sellerInfo.email,
                numberPhone: sellerInfo.numberPhone,
            };

            await api
                .put("/api/users/" + sellerInfo.id, updateData, {
                    headers: {
                        Authorization: `Bearer ${await getTokenSellertoRequest()}`,
                    },
                })
                .then((response: any) => {
                    setLoading(false);
                    Swal.fire({
                        icon: "success",
                        title: "Sucesso!",
                        text: "Informações atualizadas com sucesso",
                    });
                    setIsEditing(false);
                    // Recarregar os dados atualizados
                    getSellerInfo();
                })
                .catch((err: any) => {
                    setLoading(false);
                    console.log("Erro ao atualizar informações:", err);
                    Swal.fire({
                        icon: "error",
                        title: "Erro!",
                        text: "Erro ao atualizar informações. Tente novamente.",
                    });
                });
        } catch (error) {
            setLoading(false);
            console.log("Erro geral:", error);
            Swal.fire({
                icon: "error",
                title: "Erro!",
                text: "Erro inesperado. Tente novamente.",
            });
        }
    };

    useEffect(() => {
        getConfig();
    }, []);

    const [maskTelephone, setMaskTelephone] = useState("(99) 9999-9999");

    function changePhone(newPhone: string) {
        const phoneValidated = newPhone.replaceAll("_", "");
        if (
            phoneValidated.length >= 14 &&
            phoneValidated[13] !== "_" &&
            phoneValidated.includes("(")
        ) {
            setMaskTelephone("(99) 99999-9999");
        } else if (phoneValidated.length >= 11 && !phoneValidated.includes("(")) {
            setMaskTelephone("(99) 99999-9999");
        } else {
            setMaskTelephone("(99) 9999-9999");
        }

        if (phoneValidated.length >= 11) {
            setMaskTelephone("(99) 99999-9999");
        }
    }




    if (loading) {
        return (
            <div style={{ width: "100%", height: "calc(100vh)" }}>
                <Loading
                    type="spinningBubbles"
                    color="#FFF"
                    width={64}
                    height={64}
                />
            </div>
        );
    }

    return (
        <div className="seller-info-page">
            <div className="content-seller-info">
                <div className="title-seller-info">
                    <h2>Informações Pessoais</h2>
                    <svg
                        width="17"
                        height="17"
                        viewBox="0 0 17 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        onClick={() => navigate(-1)}
                        style={{ cursor: "pointer" }}
                    >
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0.978551 2.21657L6.957 8.2395L0.978551 14.2624L2.97137 16.2701L8.94981 10.2471L14.9283 16.2701L16.9211 14.2624L10.9426 8.2395L16.9211 2.21657L14.9283 0.208931L8.94981 6.23186L2.97137 0.208931L0.978551 2.21657Z"
                            fill="#636363"
                        />
                    </svg>
                </div>

                <div className="seller-info-body">
                    <div className="info-form">
                        <div className="form-group">
                            <label>Nome Completo</label>
                            <input
                                type="text"
                                value={sellerInfo.name}
                                disabled={!isEditing}
                                onChange={(e) => setSellerInfo({ ...sellerInfo, name: e.target.value })}
                            />
                        </div>

                        <div className="form-group">
                            <label>E-mail</label>
                            <input
                                type="email"
                                value={sellerInfo.email}
                                disabled={!isEditing}
                                onChange={(e) => setSellerInfo({ ...sellerInfo, email: e.target.value })}
                            />
                        </div>

                        <div className="form-group">
                            <label>{formatLabelDocument(sellerInfo.cpf)}</label>
                            <input
                                type="text"
                                value={formatDocument(sellerInfo.cpf)}
                                disabled={true} // CPF não deve ser editável
                            />
                        </div>

                        <div className="form-group">
                            <label>Telefone</label>
                            <InputMask

                                mask={maskTelephone}
                                value={sellerInfo.numberPhone || ""}
                                onBeforeInputCapture={(event) =>
                                    changePhone(event.currentTarget.value)
                                }
                                inputMode="numeric"
                                type="tel"
                                onChange={(e: any) => setSellerInfo({ ...sellerInfo, numberPhone: e.target.value })}

                            />

                        </div>

                        <div className="form-group">
                            <label>Status da Conta</label>
                            <div className={`status-badge ${sellerInfo.status ? 'ativo' : 'inativo'}`}>
                                {sellerInfo.status ? 'Ativo' : 'Inativo'}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Permissão de Desconto</label>
                            <div className={`status-badge ${sellerInfo.allowDiscount ? 'ativo' : 'inativo'}`}>
                                {sellerInfo.allowDiscount ? 'Habilitado' : 'Desabilitado'}
                            </div>
                        </div>

                        {sellerInfo.allowDiscount && (
                            <div className="form-group">
                                <label>Valor Máximo de Desconto (%)</label>
                                <input
                                    type="number"
                                    value={sellerInfo.discountValue || 0}
                                    disabled={true} // Somente leitura por segurança
                                />
                            </div>
                        )}

                    </div>

                    <div className="action-buttons">
                        {!isEditing ? (
                            <MainButton
                                color={
                                    config?.webappcolor.fontbutton
                                        ? config.webappcolor.fontbutton
                                        : "#636363"
                                }
                                backgroundColor={
                                    config?.webappcolor.webappbutton
                                        ? config.webappcolor.webappbutton
                                        : "#EAEAEA"
                                }
                                text="Editar Informações"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : (
                            <>
                                <MainButton
                                    color="#fff"
                                    backgroundColor="#28a745"
                                    text="Salvar Alterações"
                                    onClick={handleSaveInfo}
                                />
                                <MainButton
                                    color={
                                        config?.webappcolor.fontbutton
                                            ? config.webappcolor.fontbutton
                                            : "#636363"
                                    }
                                    backgroundColor="#6c757d"
                                    text="Cancelar"
                                    onClick={() => {
                                        setIsEditing(false);
                                        getSellerInfo(); // Recarregar dados originais
                                    }}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
